import React from 'react';
import PropTypes from 'prop-types';
import { FormField, InputTextField, Typography } from '@tempmee/react';

/**
 * A single calendar-date field.
 *
 * `type="date"` deliberately. The design system has no DatePicker — its
 * `calendar` export is a shift/availability month grid, not a form field — and
 * the native control's value is exactly `YYYY-MM-DD`, which is the
 * awards-service wire format. That means no parsing, no formatting, and no
 * timezone hazard: `new Date('2026-08-15')` would be UTC midnight and render as
 * August 14th in America/New_York.
 *
 * The date type rides on the DS's own `InputTextField` rather than a bare
 * `<input>`. `InputTextFieldProps extends Omit<InputHTMLAttributes, 'size'>`
 * and the component spreads `...props` onto the real input, so `type="date"`
 * reaches it intact — the control is still native, it just wears the design
 * system's frame instead of whatever the global cascade happens to supply.
 *
 * That was not cosmetic. As a bare `<input>` this picked up the stray global
 * `input { }` rule in themes/style.module.scss (see designSystemCompat.css),
 * which painted it as a grey inset box beside the DS-framed text field
 * directly above it in the same dialog. Going through the DS also drops the
 * last native replaceable element in new code, so the betterer exception this
 * file used to carry is gone.
 *
 * Note on DS components: the package's `Label` is a small uppercase status
 * chip (renders a <span>, no `htmlFor`) — not a form label — so it cannot
 * associate with the input. `FormField` is the DS building block that renders
 * a real `<label htmlFor>` plus a `required` asterisk, so it is used here
 * instead. `Typography`'s `color` prop is a raw CSS color value (applied via
 * inline style), not a semantic enum, so the error/secondary helper-text
 * color is applied via the DS's own `text-error` / `text-text-secondary`
 * utility classes (the same classes the DS `TextField` component uses for
 * this exact state) rather than an invented `color` value.
 */
export const DateField = ({
  id,
  label,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
}) => {
  const describedBy = helperText ? `${id}-helper` : undefined;

  return (
    <FormField label={label} htmlFor={id} required={required}>
      <InputTextField
        id={id}
        type="date"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        required={required}
        isError={error}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
      />

      {helperText ? (
        <Typography
          id={describedBy}
          variant="caption"
          className={error ? 'text-error' : 'text-text-secondary'}
        >
          {helperText}
        </Typography>
      ) : null}
    </FormField>
  );
};

DateField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  /** `YYYY-MM-DD`, or an empty string. */
  value: PropTypes.string,
  /** Receives the `YYYY-MM-DD` string, not the change event. */
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

DateField.defaultProps = {
  value: '',
  error: false,
  helperText: undefined,
  required: false,
  disabled: false,
};
